import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isDrivePath } from '../lib/drive/head';
import { isSanitarioPath } from '../lib/sanitario/head';
import { isVisitePath } from '../lib/visite/head';
import { isOssPath } from '../lib/oss/head';
import { isNativeOssApp } from '../lib/oss/bridge';
import { isNativeSanitarioApp } from '../lib/sanitario/bridge';
import { Button } from './ui/button';
import { Download, X } from 'lucide-react';

export function isNativeFamilyApp() {
  return typeof navigator !== "undefined" && /AffidyaFamily(Android|IOS)\//.test(navigator.userAgent || "");
}

export function isNativeFamilyAndroidApp() {
  return isNativeFamilyApp();
}

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const { pathname } = useLocation();
  // Drive (/autista*) ha un proprio manifest e un proprio invito all'installazione
  const inDrive = isDrivePath(pathname);
  const inSanitario = isSanitarioPath(pathname);
  const inVisite = isVisitePath(pathname);
  const inOss = isOssPath(pathname);
  
  // Check if already installed on initial render
  const isInstalled = typeof window !== 'undefined' && (
    window.matchMedia('(display-mode: standalone)').matches ||
    isNativeFamilyApp() ||
    isNativeOssApp() ||
    isNativeSanitarioApp()
  );

  useEffect(() => {
    // Skip if already installed
    if (isInstalled || inDrive || inSanitario || inVisite || inOss) return;

    // Check if dismissed recently (24h)
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      if (Date.now() - dismissedTime < 24 * 60 * 60 * 1000) {
        return;
      }
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setShowPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled, inDrive, inSanitario, inVisite, inOss]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('AFFIDYA installata!');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    setShowPrompt(false);
  };

  if (!showPrompt || isInstalled || inDrive || inVisite || inOss) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 z-50 animate-in slide-in-from-bottom-5">
      <button 
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"
      >
        <X className="w-4 h-4" />
      </button>
      
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
          <img src="/icon-512.png" alt="AFFIDYA" className="w-8 h-8 rounded" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-slate-900 text-sm">Installa AFFIDYA</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Aggiungi alla schermata Home per un accesso rapido
          </p>
        </div>
      </div>
      
      <div className="flex gap-2 mt-4">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleDismiss}
          className="flex-1 h-9 text-xs"
        >
          Non ora
        </Button>
        <Button 
          size="sm" 
          onClick={handleInstallClick}
          className="flex-1 h-9 text-xs bg-emerald-600 hover:bg-emerald-700"
        >
          <Download className="w-3 h-3 mr-1" />
          Installa
        </Button>
      </div>
    </div>
  );
}
